/*
  Столбики графиков — вкладка «Графики» на «Отчётах» (§3.11.2).

  Перенесено 29.09.2026 из макетов, выбранных владельцем
  (`макеты-графиков-дашборда` на рабочем столе). Рисуется своим SVG, без
  библиотеки: кабинету нужны шесть видов графиков, а любая из библиотек тянет за
  собой сотни килобайт, своё оформление и свой способ подписывать оси - то есть
  второй язык вёрстки рядом с нашим.

  🔴 **Рисунок считается в настоящих пикселях** (`useChartWidth`, 30.09.2026).
  До этого рамка была условной и растягивалась по ширине карточки вместе с
  подписями: один и тот же шрифт выходил 7,6 px в узкой карточке и 17,7 px в
  широкой - больше заголовка. Подробности и замеры - в шапке `useChartWidth.js`.

  🔴 **Цвет задаётся классом, а не атрибутом `fill`.** Тот же цвет уже записан в
  `styles.css`, и вторая его запись здесь разъехалась бы при первой правке
  оформления (правило 6 скилла `frontend`).

  Подписывается не каждый столбик, а только названные в `mark`: число над каждым
  превращает график в таблицу, набранную мельче таблицы.
*/

import useChartWidth from './useChartWidth.js'

const PAD_X = 24
/*
  Поля по краям обязательны: без них крайняя подпись обрезается ровно
  наполовину. В коде это выглядит правильно, видно только в браузере - найдено
  осмотром макетов 29.09.2026.
*/
const EDGE = 40

/*
  Подпись у края рамки прижимается к ней, а не центрируется по столбику: иначе
  половина текста уезжает за границу картинки и пропадает.
*/
function anchor(x, width) {
  if (x < EDGE) return { x: 0, textAnchor: 'start' }
  if (x > width - EDGE) return { x: width, textAnchor: 'end' }
  return { x: x.toFixed(1), textAnchor: 'middle' }
}

/*
  Столбики одного ряда - одного цвета: два цвета означали бы две разные
  величины, а здесь величина одна (§4.5.3, «цвет несёт смысл»).
*/
export function VerticalBars({ rows, every = 0, mark = [], height = 220, maxBar = 90, tone, alt }) {
  const [measure, width] = useChartWidth()
  if (!rows.length) return null

  const padTop = 24
  const padBottom = 28
  const plot = height - padTop - padBottom
  const top = Math.max(...rows.map((row) => Number(row.value)), 0)
  const step = (width - PAD_X * 2) / rows.length
  /*
    Потолок ширины столбика: два столбика на всю рамку читаются не как график, а
    как две заливки. Узкий столбик с полем по бокам честнее.
  */
  const barWidth = Math.min(Math.max(3, step - 2), maxBar)
  const center = (index) => PAD_X + index * step + (step - 2) / 2

  return (
    <div className="chart__frame" ref={measure}>
      <svg className="chart__svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={alt}>
        <line
          className="chart__base"
          x1={PAD_X - 8}
          y1={padTop + plot}
          x2={width - PAD_X + 8}
          y2={padTop + plot}
        />
        {rows.map((row, index) => {
          const tall = top === 0 ? 0 : (Number(row.value) / top) * plot
          const x = center(index) - barWidth / 2
          const y = padTop + plot - tall
          return (
            <g key={row.key ?? row.title}>
              {/* Подсказка мышью: подписан не каждый столбик, а навести на
                  любой человек пробует первым делом. */}
              <title>
                {row.title}: {row.caption}
                {row.note ? ` (${row.note})` : ''}
              </title>
              <rect
                className={`chart__bar${tone ? ` chart__bar--${tone}` : ''}`}
                x={x}
                y={y}
                width={barWidth}
                /* 🔴 Три пикселя, а не ноль и не один. Осмотр 29.09.2026: рядом
                   с 2,8 млн столбики на 18 и 21 тысячу дают шесть десятых
                   пикселя - на экране их нет вовсе, и подписи с суммами висят над
                   пустотой. Обрубок в три пикселя видно, и он по-прежнему читается
                   как «почти ничего»; точную величину говорит подпись, а не
                   высота. */
                height={Math.max(tall, 3)}
                rx={Math.min(4, barWidth / 2)}
              />
              {every && index % every === 0 ? (
                <text className="chart__ax" {...anchor(center(index), width)} y={height - 10}>
                  {row.short ?? row.title}
                </text>
              ) : null}
              {mark.includes(index) ? (
                <text className="chart__val" {...anchor(center(index), width)} y={y - 6}>
                  {row.hero ?? row.caption}
                </text>
              ) : null}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/* Полосы в строку: для названий, которые вертикально не подписать. */
export function HorizontalBars({ rows, alt }) {
  const [measure, width] = useChartWidth()
  if (!rows.length) return null

  const rowHeight = 30
  const height = rows.length * rowHeight + 6
  /*
    Колонки названия и суммы - доли ширины, а не пиксели: на узком экране
    фиксированные 250 пикселей под название съедали бы всю полосу, и график
    превращался бы в список с обрубками вместо полос.
  */
  const labelWidth = Math.min(Math.max(width * 0.3, 120), 320)
  const valueWidth = Math.min(Math.max(width * 0.16, 90), 150)
  const space = width - labelWidth - valueWidth
  const top = Math.max(...rows.map((row) => Number(row.value)), 0)

  return (
    <div className="chart__frame" ref={measure}>
      <svg className="chart__svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={alt}>
        {rows.map((row, index) => {
          const y = index * rowHeight + 6
          const long = top === 0 ? 2 : Math.max((Number(row.value) / top) * space, 2)
          return (
            <g key={row.key ?? row.title}>
              <title>
                {row.title}: {row.caption}
                {row.note ? ` (${row.note})` : ''}
              </title>
              {/* 🔴 Волосяная черта между строками (30.09.2026, замечание
                  владельца: «глазами плохо воспринимается столько данных и какая
                  цифра к чему относится»). Название стоит слева, сумма справа, и
                  между ними полоса шириной в пол-экрана - на двадцати семи
                  услугах взгляд теряет строку и читает чужое число. Под
                  последней строкой черты нет: она отделяла бы список от
                  пояснения, а это не соседние строки списка. */}
              {index < rows.length - 1 ? (
                <line
                  className="chart__row-line"
                  x1="0"
                  y1={y + rowHeight - 6}
                  x2={width}
                  y2={y + rowHeight - 6}
                />
              ) : null}
              <text className="chart__name" x="0" y={y + 14}>
                {row.title}
              </text>
              <rect
                className="chart__bar"
                x={labelWidth}
                y={y + 4}
                width={long}
                height="14"
                rx="4"
              />
              <text className="chart__val" x={width} y={y + 15} textAnchor="end">
                {row.caption}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
